import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import Header from './Header'
import SideMenu from './SideMenu'
import { createContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { useAppSelector } from '../store/hooks'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { load, save } from '../utils/auth'
import { useIntl } from 'react-intl'

const SIDEBAR_COLLAPSED_COOKIE = 'lty_sidebar_collapsed'
const SIDEBAR_COLLAPSED_COOKIE_DAYS = 365

type AppContextType = {
   isMenuOpen: boolean
   isSidebarCollapsed: boolean
   toggleMenu: () => void
   toggleSidebar: () => void
}
export const AppContext = createContext<AppContextType>({
   isMenuOpen: false,
   isSidebarCollapsed: false,
   toggleMenu: () => {},
   toggleSidebar: () => {},
})

type Props = {}

const SidebarToggleButton = ({
   isSidebarCollapsed,
   onToggle,
}: {
   isSidebarCollapsed: boolean
   onToggle: () => void
}) => {
   const intl = useIntl()

   return createPortal(
      <button
         type="button"
         onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            onToggle()
         }}
         aria-label={intl.formatMessage({
            id: isSidebarCollapsed ? 'titles.expandMenu' : 'titles.collapseMenu',
         })}
         aria-expanded={!isSidebarCollapsed}
         className="hidden lg:flex items-center justify-center h-7 w-7 rounded-full bg-white border border-zinc-200 shadow-md text-zinc-400 hover:text-zinc-500 hover:shadow-lg cursor-pointer"
         style={{
            position: 'fixed',
            top: '5rem',
            left: isSidebarCollapsed ? '4rem' : '17rem',
            transform: 'translateX(-50%)',
            zIndex: 200,
         }}
      >
         <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
            style={{ transform: isSidebarCollapsed ? 'rotate(180deg)' : undefined, pointerEvents: 'none' }}
         >
            <path
               d="M7.25 2.75 3.75 6l3.5 3.25"
               stroke="currentColor"
               strokeWidth="1.4"
               strokeLinecap="round"
               strokeLinejoin="round"
            />
         </svg>
      </button>,
      document.body,
   )
}

export const Layout = (props: Props) => {
   const [isMenuOpen, setIsMenuOpen] = useState(false)
   const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => load(SIDEBAR_COLLAPSED_COOKIE) === 'true')
   const { pathname } = useLocation()
   const navigate = useNavigate()
   const auth = useAppSelector((state) => state.authToken)
   const showDesktopSidebar = Boolean(auth && auth.isAuthenticated && !isSidebarCollapsed)

   useEffect(() => {
      setIsMenuOpen(false)
      document.body.classList.remove('overflow-hidden', 'lg:overflow-auto')
      if (!auth.isPristine) {
         if (!auth || !auth.isAuthenticated) {
            return navigate(`/accounts`)
         }
      }
   }, [pathname, auth, navigate])

   useEffect(() => {
      save(SIDEBAR_COLLAPSED_COOKIE, String(isSidebarCollapsed), SIDEBAR_COLLAPSED_COOKIE_DAYS)
   }, [isSidebarCollapsed])

   function toggleMenu() {
      setIsMenuOpen((current) => {
         if (!current) {
            document.body.classList.add('overflow-hidden', 'lg:overflow-auto')
         } else {
            document.body.classList.remove('overflow-hidden', 'lg:overflow-auto')
         }

         return !current
      })
   }

   function toggleSidebar() {
      setIsSidebarCollapsed((current) => !current)
   }

   return (
      <>
         <AppContext.Provider value={{ isMenuOpen, isSidebarCollapsed, toggleMenu, toggleSidebar }}>
            <Header onToggleMenu={toggleMenu} isMenuOpen={isMenuOpen} />
            <div
               className={classNames('h-full', {
                  'lg:ml-[17rem]': showDesktopSidebar,
                  'lg:ml-16': auth && auth.isAuthenticated && isSidebarCollapsed,
               })}
            >
               {auth && auth.isAuthenticated && <SideMenu />}
               <div className="relative h-full pt-16 bg-gray-100">
                  <Outlet />
                  <ToastContainer />
               </div>
            </div>
            {auth && auth.isAuthenticated && (
               <SidebarToggleButton isSidebarCollapsed={isSidebarCollapsed} onToggle={toggleSidebar} />
            )}
         </AppContext.Provider>
      </>
   )
}
